import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CommandStatus, DiagnosticsStep } from '../../../../../api/api-reference';
import { DiagnosticsStepResult } from '../../../../../services/diagnostics-state.service';

interface DiagnosticsStepRow {
  labelKey: string;
  // Rows without steps are not produced by the backend yet and stay pending. The hierarchy row
  // covers several probes, so a row can hold more than one.
  steps?: DiagnosticsStep[];
}

interface DiagnosticsStepView extends DiagnosticsStepRow {
  results: DiagnosticsStepResult[];
}

@Component({
  selector: 'app-diagnostics-modal',
  standalone: true,
  imports: [TranslateModule, CommonModule, AngularSvgIconModule],
  templateUrl: './diagnostics-modal.component.html',
  styleUrl: './diagnostics-modal.component.scss'
})
export class DiagnosticsModalComponent {
  @Input() status?: CommandStatus;
  @Input() errorMessage?: string;
  @Input() steps: DiagnosticsStepResult[] = [];
  @Output() closeSignal = new EventEmitter<boolean>();

  readonly stepRows: DiagnosticsStepRow[] = [
    { labelKey: 'DIAGNOSTICS_STEP_TARGET_METER_STATUS', steps: [DiagnosticsStep.TargetMeterPing] },
    { labelKey: 'DIAGNOSTICS_STEP_TELEMETRY_HISTORY', steps: [DiagnosticsStep.TelemetryHistory] },
    {
      labelKey: 'DIAGNOSTICS_STEP_HIERARCHY_TRACE',
      steps: [DiagnosticsStep.ParentMeterPing, DiagnosticsStep.GrandparentMeterPing],
    },
    { labelKey: 'DIAGNOSTICS_STEP_OUTAGE_SWEEP', steps: [DiagnosticsStep.NeighbourSweepPing] },
  ];

  get isRunning(): boolean {
    return this.status === CommandStatus.Pending || this.status === CommandStatus.InProgress;
  }

  // Merged here rather than in the template: @let needs Angular 18.
  get stepViews(): DiagnosticsStepView[] {
    return this.stepRows.map((row) => ({
      ...row,
      results: (row.steps ?? [])
        .map((step) => this.steps.find((result) => result.step === step))
        .filter((result): result is DiagnosticsStepResult => !!result),
    }));
  }

  // The hierarchy row names the meter's place in the tree, the others name the meter itself.
  labelKeyFor(result: DiagnosticsStepResult): string {
    switch (result.step) {
      case DiagnosticsStep.ParentMeterPing:
        return 'DIAGNOSTICS_PARENT';
      case DiagnosticsStep.GrandparentMeterPing:
        return 'DIAGNOSTICS_GRANDPARENT';
      default:
        return 'DIAGNOSTICS_METER_NUMBER';
    }
  }

  isTelemetryStep(result: DiagnosticsStepResult): boolean {
    return result.step === DiagnosticsStep.TelemetryHistory;
  }

  missingReads(result: DiagnosticsStepResult): number {
    return Math.max(0, (result.expectedReadCount ?? 0) - (result.actualReadCount ?? 0));
  }

  isSweepStep(result: DiagnosticsStepResult): boolean {
    return result.step === DiagnosticsStep.NeighbourSweepPing;
  }

  isHierarchyStep(result: DiagnosticsStepResult): boolean {
    return result.step === DiagnosticsStep.ParentMeterPing
      || result.step === DiagnosticsStep.GrandparentMeterPing;
  }

  isFailed(result: DiagnosticsStepResult): boolean {
    return result.status === CommandStatus.Failed || result.status === CommandStatus.ErrorOccured;
  }

  getStepStatusKey(result: DiagnosticsStepResult): string {
    return this.isFailed(result) ? 'DIAGNOSTICS_PING_FAILED' : 'DIAGNOSTICS_PING_SUCCESSFUL';
  }

  toggleModal(): void {
    this.closeSignal.emit();
  }
}
